import { contentsGridFor, getItemDef, type GridState } from "@rietz/shared";
import { setContainerSearch, useHud, type OpenContainer, type SearchView } from "../store.js";

/**
 * Searching what you have opened. A world container opens COVERED; clicking the
 * cover lifts it, every item becomes an inert "?" and a lens walks the grid in
 * reading order revealing one every `SEARCH_REVEAL_MS`. Fully revealed, the
 * grid stays searched for the raid. PRESENTATION ONLY — the server sends the
 * whole grid in `containerState`; nothing is sent or trusted from the wire.
 *
 * The unit of "searched" is a TARGET: a crate is one (`<id>`), a body up to
 * three (`<id>#pockets`, `<id>#<rig iid>`, `<id>#<backpack iid>`); the seven
 * equipment slots are never covered. `searchTargetsOf` mints the keys and
 * `searchTargetId` maps a displayed grid back onto one.
 *
 * Memory: one entry per target opened THIS RAID — done, and which iids are
 * still hidden. The hidden set is taken ONCE on first sight; items appearing
 * later are never hidden (a split mints a provisional id the next `inv`
 * replaces). Container ids REPEAT across raids, so `resetContainerSearch` must
 * run at every raid boundary (`InventoryController.reset()`). The sweep is a
 * `setTimeout` chain, not rAF; the gliding lens is a CSS transition.
 */

/** The dwell before the first item gives itself up — the lid coming off. */
export const SEARCH_FIRST_REVEAL_MS = 550;
/** One item per this long thereafter. */
export const SEARCH_REVEAL_MS = 750;

/** One thing that can be rummaged through: an id to remember it by, and the
 *  grid whose items the sweep reveals. */
export interface SearchTarget {
  id: string;
  grid: GridState;
}

/** A displayed grid, as the drop layer addresses it. The two shapes
 *  `GridPanelTarget` reduces to once the player's own areas are excluded. */
export type SearchWhere = { area: "container" } | { area: "inside"; boxIid: string };

/** The bags a body may be wearing, in doll order — the two worn slots whose
 *  contents are looted through the corpse rather than lifted off it. */
const BODY_BAG_SLOTS = ["rig", "backpack"] as const;

/**
 * Every target the open container has, in draw order: one for a crate, up to
 * three for a body (a worn bag only when it resolves as a container). The stash
 * has none.
 */
export function searchTargetsOf(container: OpenContainer | null): SearchTarget[] {
  if (container === null || container.kind === "stash") return [];
  const equipment = container.equipment;
  if (equipment === undefined) return [{ id: container.id, grid: container.grid }];

  const targets: SearchTarget[] = [{ id: `${container.id}#pockets`, grid: container.grid }];
  for (const slot of BODY_BAG_SLOTS) {
    const bag = equipment[slot];
    if (bag === null) continue;
    const def = getItemDef(bag.defId);
    if (def === null || def.kind !== "container") continue;
    // `contentsGridFor` mints the empty grid for an empty bag — an empty target
    // still has to be rummaged.
    targets.push({ id: `${container.id}#${bag.iid}`, grid: contentsGridFor(def, bag) });
  }
  return targets;
}

/**
 * Which target a displayed grid belongs to, or `null` for nobody's business:
 * nothing open, or a box merely LYING in a container — found by the sweep over
 * its grid, not searched separately.
 */
export function searchTargetId(
  container: OpenContainer | null,
  where: SearchWhere,
): string | null {
  if (container === null || container.kind === "stash") return null;
  const equipment = container.equipment;
  if (where.area === "container") {
    return equipment === undefined ? container.id : `${container.id}#pockets`;
  }
  if (equipment === undefined) return null;
  for (const slot of BODY_BAG_SLOTS) {
    const bag = equipment[slot];
    if (bag !== null && bag.iid === where.boxIid) return `${container.id}#${bag.iid}`;
  }
  return null;
}

/**
 * Whether that target has given up everything. An id with no view counts as
 * done, so screens that never engage the machinery draw as before.
 */
export function searchTargetDone(
  views: Readonly<Record<string, SearchView>>,
  id: string | null,
): boolean {
  if (id === null) return true;
  const view = views[id];
  return view === undefined || view.phase === "done";
}

/** Every target of the open container searched — the question the Werkbank asks
 *  before it will list a corpse's parts as compatible ones you have. */
export function allSearched(
  container: OpenContainer | null,
  views: Readonly<Record<string, SearchView>>,
): boolean {
  return searchTargetsOf(container).every((t) => searchTargetDone(views, t.id));
}

/**
 * Which destinations are shut because unsearched — `computeHover`'s refusal
 * predicate, per drawer for a body. Reads the store at call time; the sweep
 * finishes between pointer moves.
 */
export function searchDropLock(container: OpenContainer | null): (where: SearchWhere) => boolean {
  return (where) =>
    !searchTargetDone(useHud.getState().containerSearch, searchTargetId(container, where));
}

/** What is left to find in one target, and whether anything is. */
interface SearchMemory {
  searched: boolean;
  unrevealed: Set<string>;
}

/**
 * Per-raid memory keyed by TARGET id, wiped by `resetContainerSearch` — the ids
 * come back.
 */
const memory = new Map<string, SearchMemory>();

/** The one running sweep, if any. There is only ever one target being rummaged
 *  — two lenses crawling two grids at once is a slot machine, not a search. */
let timer: ReturnType<typeof setTimeout> | null = null;
/** Which target the running sweep belongs to. `null` when none runs. */
let sweepId: string | null = null;

function stopSweep(): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  sweepId = null;
}

/** Reading order — the order a person's eyes go over a grid. */
function readingOrder(grid: GridState): { iid: string; x: number; y: number }[] {
  return grid.items
    .map((p) => ({ iid: p.item.iid, x: p.x, y: p.y }))
    .sort((a, b) => (a.y === b.y ? a.x - b.x : a.y - b.y));
}

/** Iids still hidden, in reading order, of the items ACTUALLY in the grid now. */
function hiddenOf(grid: GridState, mem: SearchMemory): string[] {
  return readingOrder(grid)
    .filter((p) => mem.unrevealed.has(p.iid))
    .map((p) => p.iid);
}

function sameView(a: SearchView | undefined, b: SearchView | undefined): boolean {
  if (a === undefined || b === undefined) return a === b;
  if (a.phase !== b.phase || a.focusIid !== b.focusIid) return false;
  if (a.hiddenIids.length !== b.hiddenIids.length) return false;
  return a.hiddenIids.every((iid, i) => iid === b.hiddenIids[i]);
}

function sameMap(
  a: Readonly<Record<string, SearchView>>,
  b: Readonly<Record<string, SearchView>>,
): boolean {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((key) => sameView(a[key], b[key]));
}

/** Writes only on a real change — see why in `syncContainerSearch`. */
function publish(next: Readonly<Record<string, SearchView>>): void {
  if (sameMap(useHud.getState().containerSearch, next)) return;
  setContainerSearch(next);
}

/** The memory for a target, minting it (and its hidden snapshot) on first sight. */
function memoryFor(target: SearchTarget): SearchMemory {
  let mem = memory.get(target.id);
  if (mem === undefined) {
    mem = { searched: false, unrevealed: new Set(target.grid.items.map((p) => p.item.iid)) };
    memory.set(target.id, mem);
  }
  return mem;
}

/**
 * Brings the store's search map in line with the container. Called on EVERY
 * `container` change, so it must be cheap and must not write unless something
 * drawn moved. `null` cancels a running sweep and clears the map, keeping the
 * memory.
 */
export function syncContainerSearch(container: OpenContainer | null): void {
  // The stash is one's own cupboard, and the menu screens must never see this
  // machinery. `searchTargetsOf` says so by returning nothing for it.
  const targets = searchTargetsOf(container);
  if (targets.length === 0) {
    stopSweep();
    publish({});
    return;
  }

  // The sweep's target went away without the panel closing (next crate, rig
  // pulled off mid-rummage).
  if (sweepId !== null && !targets.some((t) => t.id === sweepId)) stopSweep();

  const prev = useHud.getState().containerSearch;
  const next: Record<string, SearchView> = {};
  for (const target of targets) {
    const mem = memoryFor(target);
    const searching = sweepId === target.id;
    const phase: SearchView["phase"] = searching ? "searching" : mem.searched ? "done" : "covered";
    const hiddenIids = hiddenOf(target.grid, mem);
    // The lens survives a re-sync of its target, starts at the next hidden item
    // for a fresh sweep, and is dropped for every other target.
    const focusIid = searching ? prev[target.id]?.focusIid ?? hiddenIids[0] ?? null : null;
    next[target.id] = { phase, hiddenIids, focusIid };
  }
  publish(next);
}

/**
 * The player clicked a cover: lifts it and starts its sweep. Refuses unless
 * store map and container agree the target is covered (a stale click). Starting
 * one STOPS whatever else was being swept; that target keeps its progress.
 */
export function beginContainerSearch(targetId: string): void {
  const s = useHud.getState();
  if (s.containerSearch[targetId]?.phase !== "covered") return;
  const target = searchTargetsOf(s.container).find((t) => t.id === targetId);
  if (target === undefined) return;
  const mem = memory.get(targetId);
  if (mem === undefined) return;

  stopSweep();
  sweepId = targetId;
  // A whole re-derivation: starting here ENDS whatever was running, and only
  // the
  // sync can say that for every target at once.
  syncContainerSearch(s.container);
  timer = globalThis.setTimeout(tick, SEARCH_FIRST_REVEAL_MS);
}

/** One reveal. Re-reads the world every time, because it moves between ticks. */
function tick(): void {
  timer = null;
  const id = sweepId;
  if (id === null) return;

  const s = useHud.getState();
  // The panel closed, or the target is gone. Whatever caused that already ran
  // `syncContainerSearch`; the memory keeps the progress.
  const target = searchTargetsOf(s.container).find((t) => t.id === id);
  const mem = memory.get(id);
  if (target === undefined || mem === undefined) {
    stopSweep();
    return;
  }

  // Items another looter took while we were looking are simply no longer there
  // to reveal, which is also what stops the sweep hanging on them forever.
  const remaining = hiddenOf(target.grid, mem);
  if (remaining.length === 0) {
    finish(mem, id, s.containerSearch);
    return;
  }

  mem.unrevealed.delete(remaining[0] as string);
  const left = remaining.slice(1);
  if (left.length === 0) {
    finish(mem, id, s.containerSearch);
    return;
  }
  publish({
    ...s.containerSearch,
    [id]: { phase: "searching", hiddenIids: left, focusIid: left[0] ?? null },
  });
  timer = globalThis.setTimeout(tick, SEARCH_REVEAL_MS);
}

/** Nothing left to find: that target is searched for the rest of the raid. */
function finish(
  mem: SearchMemory,
  id: string,
  views: Readonly<Record<string, SearchView>>,
): void {
  mem.searched = true;
  mem.unrevealed.clear();
  stopSweep();
  publish({ ...views, [id]: { phase: "done", hiddenIids: [], focusIid: null } });
}

/**
 * Forgets everything searched this raid. Called from
 * `InventoryController.reset()` only — a dev respawn stays in the same raid
 * with the same crates.
 */
export function resetContainerSearch(): void {
  stopSweep();
  memory.clear();
  setContainerSearch({});
}
